import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { api, CATEGORIES, formatApiError, posterSrc } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { DialogFooter } from "./ui/dialog";
import { Plus, Trash2, Upload, ImageIcon } from "lucide-react";

export const emptyTier = () => ({ name: "General", price: 500, quantity: 100, seated: false, rows: 5, cols: 10 });

export const emptyForm = () => ({ title: "", description: "", category: "Music", city: "", venue: "", date: "", poster_url: "", tiers: [emptyTier()] });

export function eventToForm(e) {
  const d = new Date(e.date);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  return { title: e.title, description: e.description, category: e.category, city: e.city, venue: e.venue, date: local, poster_url: e.poster_url,
    tiers: e.tiers.map((t) => ({ name: t.name, price: t.price, quantity: t.quantity, seated: t.seated, rows: t.rows || 5, cols: t.cols || 10, sold: t.sold })) };
}

const cls = "mt-1 border-zinc-800 bg-[#2F0D18]";

export default function EventForm({ form, setForm, onSubmit, onCancel, saving, submitLabel }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setTier = (i, key, val) => {
    const t = [...form.tiers];
    t[i] = { ...t[i], [key]: ["name", "seated"].includes(key) ? val : Number(val) };
    setForm({ ...form, tiers: t });
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/poster", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("poster_url", data.url);
      toast.success("Artwork uploaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-widest text-zinc-400">Event title</Label>
        <Input data-testid="ce-title" required value={form.title} onChange={(e) => set("title", e.target.value)} className={cls} />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-zinc-400">Description</Label>
        <Textarea data-testid="ce-description" required value={form.description} onChange={(e) => set("description", e.target.value)} className={cls} rows={3} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Category</Label>
          <Select value={form.category} onValueChange={(v) => set("category", v)}>
            <SelectTrigger data-testid="ce-category" className={cls}><SelectValue /></SelectTrigger>
            <SelectContent className="border-zinc-800 bg-[#33101C] text-zinc-100">{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Date & time</Label>
          <Input data-testid="ce-date" type="datetime-local" required value={form.date} onChange={(e) => set("date", e.target.value)} className={cls} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">City</Label>
          <Input data-testid="ce-city" required value={form.city} onChange={(e) => set("city", e.target.value)} className={cls} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Venue</Label>
          <Input data-testid="ce-venue" required value={form.venue} onChange={(e) => set("venue", e.target.value)} className={cls} />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-widest text-zinc-400">Event artwork</Label>
        <div className="mt-2 flex items-center gap-4">
          <div className="flex h-24 w-20 items-center justify-center overflow-hidden rounded-lg border border-zinc-800 bg-[#2F0D18]">
            {form.poster_url ? <img src={posterSrc(form.poster_url)} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-6 w-6 text-zinc-600" />}
          </div>
          <div>
            <input ref={fileRef} type="file" accept="image/*" hidden data-testid="ce-poster-file" onChange={(e) => upload(e.target.files?.[0])} />
            <Button type="button" data-testid="ce-upload-btn" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-zinc-700 bg-transparent text-zinc-100 hover:border-amber-500 hover:text-amber-400">
              <Upload className="mr-2 h-4 w-4" /> {uploading ? "Uploading…" : form.poster_url ? "Replace artwork" : "Upload artwork"}
            </Button>
            <div className="mt-1 text-[11px] text-zinc-500">JPG/PNG/WebP · portrait works best · max 8MB</div>
          </div>
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-xs uppercase tracking-widest text-zinc-400">Ticket tiers</Label>
          <Button type="button" size="sm" variant="ghost" onClick={() => setForm({ ...form, tiers: [...form.tiers, emptyTier()] })} className="h-7 text-xs text-amber-400 hover:text-amber-300"><Plus className="mr-1 h-3 w-3" />Add tier</Button>
        </div>
        <div className="space-y-2">
          {form.tiers.map((t, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-zinc-800 bg-[#2F0D18] p-2">
              <div className="grid grid-cols-[1fr,auto,auto,auto] gap-2">
                <Input data-testid={`ce-tier-name-${i}`} placeholder="Name" value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} className="border-zinc-800 bg-transparent" />
                <Input data-testid={`ce-tier-price-${i}`} type="number" min="0" placeholder="Price" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} className="w-24 border-zinc-800 bg-transparent" />
                {t.seated ? (
                  <div className="flex w-20 items-center justify-center font-mono text-xs text-zinc-400">{t.rows * t.cols} seats</div>
                ) : (
                  <Input data-testid={`ce-tier-qty-${i}`} type="number" min="1" placeholder="Qty" value={t.quantity} onChange={(e) => setTier(i, "quantity", e.target.value)} className="w-20 border-zinc-800 bg-transparent" />
                )}
                <Button type="button" variant="ghost" size="icon" onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, idx) => idx !== i) })} disabled={form.tiers.length === 1} className="text-red-400"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="flex flex-wrap items-center gap-3 px-1 text-xs text-zinc-400">
                <label className="flex items-center gap-2"><Switch data-testid={`ce-tier-seated-${i}`} checked={t.seated} onCheckedChange={(v) => setTier(i, "seated", v)} /> Reserved seating</label>
                {t.seated && (<>
                  <span>Rows <Input data-testid={`ce-tier-rows-${i}`} type="number" min="1" max="26" value={t.rows} onChange={(e) => setTier(i, "rows", e.target.value)} className="ml-1 inline-block h-7 w-16 border-zinc-800 bg-transparent" /></span>
                  <span>Seats/row <Input data-testid={`ce-tier-cols-${i}`} type="number" min="1" max="30" value={t.cols} onChange={(e) => setTier(i, "cols", e.target.value)} className="ml-1 inline-block h-7 w-16 border-zinc-800 bg-transparent" /></span>
                </>)}
                {t.sold > 0 && <span className="ml-auto font-mono text-amber-400">{t.sold} sold</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <DialogFooter>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button data-testid="ce-submit" type="submit" disabled={saving || !form.poster_url} className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">{saving ? "Saving..." : submitLabel}</Button>
      </DialogFooter>
      {!form.poster_url && <div className="text-right text-[11px] text-punch-400">Upload artwork to continue</div>}
    </form>
  );
}
